import { Token } from './lexer.types';
import { Node, TypeEnum, NullableNode } from './parser.types';

import { sanitizeInput } from './text';
import structure from './structure';

/**
 * Returns an array of top level nodes, representing the book,
 * and subsequent chapters and verses as an AST of nodes.
 */
export function parser(tokens: Token[]): Node[] {
    const rootNodes: Node[] = [];
    while (tokens.length > 0) {
        const t = tokens.shift();
        if (t) {
            const root: Node = {
                id: Math.trunc(Math.random() * 1000000),
                type: TypeEnum.book,
                value: determineBookId(String(t.value)),
                token: t,
                parent: null,
                children: [],
            };
            rootNodes.push(root);
            walk(rootNodes, root, tokens);
        }
    }
    return rootNodes;
}

function walk(rootNodes: Node[], prevNode: Node, tokens: Token[]) {
    const token = tokens.shift();
    if (token) {
        if (token.type === 'book') {
            // This is kind of an odd case, but we need to handle it.
            const newNode = processBook(rootNodes, token);
            walk(rootNodes, newNode, tokens);
        } else if (token.type === 'number') {
            const newNode = processNumber(
                rootNodes,
                token,
                tokens.length > 0 ? tokens[0] : null, // for look-ahead reference when dealing with ',' as prevNode.
                prevNode
            );
            walk(rootNodes, newNode, tokens);
        } else if (token.type === 'op') {
            const newNode = processOperator(rootNodes, token, prevNode, tokens);
            walk(rootNodes, newNode, tokens);
        }
    }
}

/**
 * Takes a string and find the book id that most closely matches the string.
 */
function determineBookId(bookToken: string): number {
    const removePeriods = true;
    bookToken = sanitizeInput(bookToken, removePeriods); // lowercase, and remove spaces
    for (const [bookID, book] of Object.entries(structure)) {
        if (book.abbr.indexOf(bookToken) > -1) {
            return Number(bookID);
        }
    }
    return 0; // means no match; unknown book id
}

function processBook(rootNodes: Node[], token: Token): Node {
    // This is kind of an odd case, but we need to handle it.
    const newNode: Node = {
        id: Math.trunc(Math.random() * 1000000),
        type: TypeEnum.book,
        value: determineBookId(String(token.value)),
        token: token,
        parent: null,
        children: [],
    };
    rootNodes.push(newNode);
    return newNode;
}

/**
 * Parses the number token and may rearrange the Nodes based on the type of the
 * number and operators that may possibly precede it.
 */
export function processNumber(
    rootNodes: Node[],
    token: Token,
    nextToken: Token | null,
    prevNode: Node
): Node {
    // This is the interesting case. We might have a
    // chapter or a verse.
    let p = findNonOpParent(prevNode);
    if (!p) {
        // This is bad.. you can't have a number without a parent.
        console.log(prevNode);
        console.log(token);
        throw new Error('No parent found for number');
    }
    //@ts-ignore
    let type: TypeEnum = token.type;
    let parent: NullableNode = prevNode;
    if (prevNode.type == TypeEnum.op) {
        switch (prevNode.token.value) {
            case ':':
                {
                    // Remove the colon operator from parent children list.
                    p.children = p.children.filter((c) => c.id !== prevNode.id);
                    type = TypeEnum.verse;
                    parent = p;
                }
                break;
            case '-':
                {
                    // @ts-ignore
                    type = p?.type; // This works for chapter or verse.
                    // Remove the dash operator from parent children list.
                    p.children = p.children.filter((c) => c.id !== prevNode.id);
                    // This gets a bit different now. Instead of the parent, we need the parent's parent.
                    if (p.parent) {
                        parent = p.parent;
                    }
                    // We now will need to fill in the missing numbers within this range.
                    for (
                        let i = Number(p.token.value) + 1;
                        i < Number(token.value);
                        i++
                    ) {
                        const n: Node = {
                            id: Math.trunc(Math.random() * 1000000),
                            type: type,
                            value: i,
                            token: { type: 'number', value: i },
                            parent: parent,
                            children: [],
                        };
                        parent.children.push(n);
                    }
                    // We will add the last one in the range at the end of this function
                    // and return that.
                }
                break;
            case ',':
                {
                    if (!nextToken || nextToken.value !== ':') {
                        // @ts-ignore
                        type = p?.type; // This works for chapter or verse.
                        // Remove the comma-operator from parent children list.
                        p.children = p.children.filter(
                            (c) => c.id !== prevNode.id
                        );
                        // This gets a bit different now. Instead of the parent,
                        // we need the parent's parent.
                        if (p.parent) {
                            parent = p.parent;
                        }
                    } else {
                        // We have a comma resetting us up to a new chapter.
                        // Number here has to be a chapter. What precedes it, is
                        // hard to say, and really not relevant.
                        type = TypeEnum.chapter;
                        // Remove the comma-operator from parent children list.
                        p.children = p.children.filter(
                            (c) => c.id !== prevNode.id
                        );
                        // p is not necessarily what we want; need to find chapter
                        if (p?.type !== TypeEnum.chapter) {
                            // We need to find the parent chapter.
                            p = findChapterParent(prevNode);
                        }
                        // TODO: Not sure if it's possible in a valid parse, but
                        // would not want to not find a chapter parent during a
                        // normal parse.
                        if (p?.parent) {
                            parent = p?.parent;
                        }
                    }

                    // TODO: probably need to do a lookahead here after all
                    // We can't do a lookahead here because we don't have the tokens.
                }
                break;
            default:
                break;
        }
    }
    if (prevNode.type == TypeEnum.book) {
        type = TypeEnum.chapter;
    }
    const newNode: Node = {
        id: Math.trunc(Math.random() * 1000000),
        type,
        value: token.value,
        token,
        parent,
        children: [],
    };
    parent.children.push(newNode);
    return newNode;
}

function processOperator(
    rootNodes: Node[],
    token: Token,
    prevNode: Node,
    tokens: Token[]
): Node {
    let newNode = prevNode;
    if (token.value != ';') {
        newNode = {
            id: Math.trunc(Math.random() * 1000000),
            type: TypeEnum.op,
            value: token.value,
            token: token,
            parent: prevNode,
            children: [],
        };
        prevNode.children.push(newNode);
    }
    return newNode;
}

/**
 * Walk up the tree looking for the first parent that
 * isn't an operator.
 */
function findNonOpParent(n: Node): NullableNode {
    if (n.token.type != 'op') {
        return n;
    } else {
        while (n?.parent) {
            n = n.parent;
            if (n.token.type != 'op') {
                return n;
            }
        }
    }
    return null;
}

/**
 * Walk up the tree and return the chapter node.
 */
function findChapterParent(n: Node): NullableNode {
    if (n.type === TypeEnum.chapter) {
        return n;
    } else {
        while (n?.parent) {
            n = n.parent;
            if (n.type === TypeEnum.chapter) {
                return n;
            }
        }
    }
    return null;
}
